// src/components/MessageLog.js
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";

/**
 * MessageLog component
 * - append-only log, exposes methods via ref:
 *    add(text, level = "info")
 *    error(text) -> shorthand for add(..., "error")
 *    clear()
 */
const MessageLog = forwardRef(function MessageLog(_, ref) {
  const [messages, setMessages] = useState([]);
  const idRef = useRef(0);

  useImperativeHandle(
    ref,
    () => ({
      add(text, level = "info") {
        const ts = new Date().toISOString();
        const id = ++idRef.current;
        setMessages((prev) => [...prev, { id, ts, text: String(text), level }]);
        if (level === "error") console.error(`[MSG ${ts}]`, text);
        else if (level === "warn") console.warn(`[MSG ${ts}]`, text);
        else console.log(`[MSG ${ts}]`, text);
      },
      error(text) {
        const ts = new Date().toISOString();
        const id = ++idRef.current;
        setMessages((prev) => [...prev, { id, ts, text: String(text), level: "error" }]);
        console.error(`[MSG ${ts}]`, text);
      },
      clear() {
        setMessages([]);
      },
    }),
    []
  );

  return (
    <div style={{
      marginTop: 12,
      border: "1px solid rgba(0,0,0,0.06)",
      borderRadius: 8,
      padding: 8,
      maxHeight: 180,
      overflow: "auto",
      background: "#fff",
      fontSize: "0.85rem",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <strong style={{ fontSize: "0.95rem" }}>Logs</strong>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { navigator.clipboard?.writeText(messages.map(m => `${m.ts} ${m.level} ${m.text}`).join("\n")); }}>Copy</button>
          <button onClick={() => setMessages([])}>Clear</button>
        </div>
      </div>

      {messages.length === 0 ? (
        <div style={{ color: "#6b7280" }}>No messages yet</div>
      ) : (
        messages.map((m) => (
          <div key={m.id} style={{ display: "flex", gap: 8, padding: "6px 4px", borderBottom: "1px solid rgba(0,0,0,0.02)" }}>
            <div style={{ minWidth: 150, fontFamily: "monospace", color: "#374151" }}>{m.ts}</div>
            <div style={{ color: m.level === "error" ? "#b91c1c" : (m.level === "warn" ? "#b45309" : "#0f172a") }}>
              {m.text}
            </div>
          </div>
        ))
      )}
    </div>
  );
});

export default MessageLog;
